/**
 * NaturaPatio Modern Header Interactions
 * Handles Mega menus, Search Flydown, Mobile Drawer, and Cart Badge Updates
 */

(function () {
  'use strict';

  function initNaturaPatioHeader() {
    const header = document.querySelector('.site-header--naturapatio');
    if (!header) return;

    // Keep promo copy usable if a selected image is unavailable.
    function hideBrokenPromoImage(image) {
      if (image.matches('.np-promo-card-image')) {
        image.closest('.np-promo-card-image-wrap').hidden = true;
      }
    }
    header.addEventListener('error', function (event) {
      if (event.target instanceof HTMLImageElement) hideBrokenPromoImage(event.target);
    }, true);
    header.querySelectorAll('.np-promo-card-image').forEach(image => {
      if (image.complete && image.naturalWidth === 0) hideBrokenPromoImage(image);
    });

    // 1. Sticky Header Scroll Effect
    function handleScroll() {
      if (window.scrollY > 8) {
        header.classList.add('np-header--scrolled');
      } else {
        header.classList.remove('np-header--scrolled');
      }
    }
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();

    // 2. Desktop Mega Menus
    const navItems = header.querySelectorAll('.np-nav-item[data-np-has-megamenu]');
    let closeTimer = null;

    function openMenu(item) {
      if (closeTimer) {
        clearTimeout(closeTimer);
        closeTimer = null;
      }
      navItems.forEach(el => {
        if (el !== item) el.classList.remove('np-nav-item--active');
      });
      item.classList.add('np-nav-item--active');
      header.classList.add('np-header--menu-open');
    }

    function closeAllMenus() {
      navItems.forEach(el => el.classList.remove('np-nav-item--active'));
      header.classList.remove('np-header--menu-open');
    }

    navItems.forEach(item => {
      item.addEventListener('mouseenter', function () {
        openMenu(item);
      });

      item.addEventListener('mouseleave', function () {
        closeTimer = setTimeout(function () {
          item.classList.remove('np-nav-item--active');
          const anyActive = Array.from(navItems).some(el => el.classList.contains('np-nav-item--active'));
          if (!anyActive) {
            header.classList.remove('np-header--menu-open');
          }
        }, 180);
      });

      // Accessible keyboard support
      const link = item.querySelector('.np-nav-link');
      if (link) {
        link.addEventListener('focus', function () {
          openMenu(item);
        });
      }
    });

    // Close when clicking outside header
    document.addEventListener('click', function (e) {
      if (!header.contains(e.target)) {
        closeAllMenus();
        closeSearch();
      }
    });

    // 3. Search Dropdown
    const searchBar = header.querySelector('[data-np-search-dropdown]');
    const searchTriggers = header.querySelectorAll('[data-np-search-trigger]');
    const searchClose = header.querySelector('[data-np-search-close]');
    const searchInput = header.querySelector('[data-np-search-input]');

    function openSearch() {
      closeAllMenus();
      if (searchBar) {
        searchBar.classList.add('np-search--open');
        if (searchInput) {
          setTimeout(() => searchInput.focus(), 150);
        }
      }
    }

    function closeSearch() {
      if (searchBar) {
        searchBar.classList.remove('np-search--open');
      }
    }

    searchTriggers.forEach(btn => {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        if (searchBar && searchBar.classList.contains('np-search--open')) {
          closeSearch();
        } else {
          openSearch();
        }
      });
    });

    if (searchClose) {
      searchClose.addEventListener('click', function (e) {
        e.preventDefault();
        closeSearch();
      });
    }

    // 4. Mobile Drawer
    const drawer = document.querySelector('[data-np-drawer]');
    const drawerOverlay = document.querySelector('[data-np-drawer-overlay]');
    const drawerOpenBtn = header.querySelector('[data-np-drawer-open]');
    const drawerCloseBtn = document.querySelector('[data-np-drawer-close]');

    function openDrawer() {
      if (drawer && drawerOverlay) {
        drawer.classList.add('np-drawer--open');
        drawerOverlay.classList.add('np-drawer--open');
        document.body.style.overflow = 'hidden';
      }
    }

    function closeDrawer() {
      if (drawer && drawerOverlay) {
        drawer.classList.remove('np-drawer--open');
        drawerOverlay.classList.remove('np-drawer--open');
        document.body.style.overflow = '';
      }
    }

    if (drawerOpenBtn) drawerOpenBtn.addEventListener('click', openDrawer);
    if (drawerCloseBtn) drawerCloseBtn.addEventListener('click', closeDrawer);
    if (drawerOverlay) drawerOverlay.addEventListener('click', closeDrawer);

    // Drawer: tapping a category label opens its submenu (like luxury retail sites),
    // and the submenu starts with a "Shop all" link to the category page.
    if (!document.getElementById('np-drawer-extra-style')) {
      const style = document.createElement('style');
      style.id = 'np-drawer-extra-style';
      style.textContent = '.np-drawer-sublink.np-drawer-sublink--all{color:#111111;font-weight:700;text-decoration:underline;text-underline-offset:3px}' +
        '.np-drawer-item--expanded>.np-drawer-item-header>.np-drawer-link{color:var(--np-header-gold,#C59B27)}' +
        '.np-drawer-link[role="button"]{cursor:pointer}' +
        '.np-drawer-link:focus-visible,.np-drawer-toggle-btn:focus-visible{outline:2px solid var(--np-header-gold,#C59B27);outline-offset:2px}';
      document.head.appendChild(style);
    }
    const titleCase = text => text.toLowerCase().replace(/(^|\s)(\S)/g, (m, space, ch) => space + ch.toUpperCase());
    document.querySelectorAll('.np-drawer-item').forEach(item => {
      const itemHeader = item.querySelector('.np-drawer-item-header');
      const label = itemHeader && itemHeader.querySelector('a.np-drawer-link');
      const chevron = itemHeader && itemHeader.querySelector('.np-drawer-toggle-btn[data-np-drawer-toggle]');
      const submenu = item.querySelector('.np-drawer-submenu');
      if (!label || !chevron || !submenu || label.hasAttribute('data-np-drawer-toggle')) return;
      const href = label.getAttribute('href') || '';
      const title = titleCase(label.textContent.trim());
      const inSubmenu = Array.from(submenu.querySelectorAll('a')).some(a => a.getAttribute('href') === href);
      if (href && href !== '#' && !inSubmenu) {
        const all = document.createElement('a');
        all.href = href;
        all.className = 'np-drawer-sublink np-drawer-sublink--all';
        all.textContent = (href.indexOf('/collections/') === 0 ? 'Shop all ' + title : 'All ' + title.toLowerCase());
        submenu.insertBefore(all, submenu.firstChild);
      }
      label.setAttribute('role', 'button');
      label.setAttribute('aria-expanded', 'false');
      label.setAttribute('data-np-drawer-toggle', '');
      chevron.setAttribute('aria-expanded', 'false');
      chevron.setAttribute('aria-label', 'Show ' + title + ' menu');
    });

    // Mobile Drawer Accordion items
    const accordionToggles = document.querySelectorAll('[data-np-drawer-toggle]');
    accordionToggles.forEach(toggle => {
      toggle.addEventListener('click', function (e) {
        e.preventDefault();
        const parentItem = toggle.closest('.np-drawer-item');
        if (parentItem) {
          const expanded = parentItem.classList.toggle('np-drawer-item--expanded');
          parentItem.querySelectorAll('[data-np-drawer-toggle]').forEach(btn => btn.setAttribute('aria-expanded', String(expanded)));
        }
      });
    });

    // 5. Global Escape Key handler
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' || e.keyCode === 27) {
        closeAllMenus();
        closeSearch();
        closeDrawer();
      }
    });

    // 6. Live Cart Badge Updates
    function updateCartBadges(count) {
      // Only touch the DOM when something really changes: the observer below
      // watches the first badge, so unconditional writes would re-trigger it forever.
      const badges = document.querySelectorAll('.np-cart-badge');
      const text = String(count);
      const display = count > 0 ? 'flex' : 'none';
      badges.forEach(badge => {
        if (badge.textContent.trim() !== text) badge.textContent = text;
        if (badge.getAttribute('data-header-cart-count') !== text) badge.setAttribute('data-header-cart-count', text);
        if (badge.classList.contains('np-cart-badge--empty') !== (count === 0)) badge.classList.toggle('np-cart-badge--empty', count === 0);
        if (badge.style.display !== display) badge.style.display = display;
      });
    }

    // Monitor for changes in theme cart elements or fetch /cart.js if needed
    const existingCartCountEl = document.querySelector('[data-header-cart-count]');
    if (existingCartCountEl) {
      const observer = new MutationObserver(function () {
        const val = parseInt(existingCartCountEl.getAttribute('data-header-cart-count') || existingCartCountEl.textContent, 10);
        if (!isNaN(val)) updateCartBadges(val);
      });
      observer.observe(existingCartCountEl, { attributes: true, childList: true, subtree: true });
    }

    // Listen to theme AJAX cart events
    ['cart:updated', 'cart:refresh', 'ajaxProduct:added'].forEach(evtName => {
      document.addEventListener(evtName, function (e) {
        fetch('/cart.js')
          .then(res => res.json())
          .then(cart => {
            if (cart && typeof cart.item_count === 'number') {
              updateCartBadges(cart.item_count);
            }
          })
          .catch(() => {});
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initNaturaPatioHeader);
  } else {
    initNaturaPatioHeader();
  }
})();
